import { TreeServiceType } from "./tree.service.type";
import { APP_TYPE, AppType } from "../app.type";
import { Row } from "./tree.util";
import { Inject, Injectable } from "@angular/core";
import {
  Manufacturer,
  SVHCItem
} from "../../../../shared/models/manufacturer";
import {
  getExpandablePartNumbers,
  getTreeChildren,
  getTopLevelItems,
  triggerWork,
} from "../api.service";

@Injectable()
export class TreeService implements TreeServiceType {
  items: Row[] = [];
  assemblies: string[] = [];
  openParts: Set<string> = new Set();
  manufacturersWithoutParts: Manufacturer[] = [];
  loading = true;
  loadingTreeRows = false;
  pages = "";
  partsOfCurrentAssembly: string[] = [];
  partsOfSearchedAssembly: string[] = [];
  currentColumns: string[] = [];

  constructor(@Inject(APP_TYPE) private app: AppType) {}

  async generateTree() {
    this.app.spinner.showSpinner();
    await triggerWork(
      this.app.customers.expectCurrent,
      this.app.customers.dataProvider,
      "generate-tree"
    );
    this.app.spinner.hideSpinner();
  }

  async getItems(page?: string) {
    this.app.spinner.showSpinner();

    if (page == null) {
      page = "1";
    }
    if (this.app.paginator.pageSize == null) {
      this.app.paginator.pageSize = 25;
    }
    const { items, assemblies, size } = await getTopLevelItems(
      this.app.customers.expectCurrent,
      page,
      this.app.paginator.pageSize.toString()
    );
    const expandablePartNumbers = await getExpandablePartNumbers(
      this.app.customers.expectCurrent,
      items
        .filter((row) => row.node != null && row.node.part != null)
        .map((row) => row.node.part.partNumber)
    );

    if (expandablePartNumbers != null) {
      items.forEach((row) => {
        if (row.node != null && row.node.part != null) {
          row.expandable = expandablePartNumbers.includes(
            row.node.part.partNumber
          );
          row.node.part.isLeaf = !row.expandable;
        }
      });
    }

    this.items = items;
    this.assemblies = assemblies;
    this.pages = size.toString();

    this.openParts.clear();

    this.loading = false;

    this.app.spinner.hideSpinner();
    return this.items;
  }

  getTreeColumns(): string[] {
    switch (this.app.RMSubmenu.section) {
      case "status":
        return [
          ...this.app.list.RM.commonColumns,
          ...this.app.list.RM.statusColumns,
        ];
      case "risk":
        return [
          ...this.app.list.RM.commonColumns,
          ...this.app.list.RM.riskColumns,
        ];
      case "likelihood":
        return [
          ...this.app.list.RM.commonColumns,
          ...this.app.list.RM.likelihoodColumns,
        ];
      case "impact":
        return [
          ...this.app.list.RM.commonColumns,
          ...this.app.list.RM.impactColumns,
        ];
      case "rohs":
        return [
          ...this.app.list.RM.commonColumns,
          ...this.app.list.RM.rohsColumns,
        ];
      case "reach":
        return [
          ...this.app.list.RM.commonColumns,
          ...this.app.list.RM.reachColumns,
        ];
      case "lead":
        return [
          ...this.app.list.RM.commonColumns,
          ...this.app.list.RM.leadColumns,
        ];
    }
  }

  async toggleTreeRow(row: Row) {
    this.loadingTreeRows = true;
    this.app.spinner.showSpinner("tree");

    if (!this.app.tree.isPartRow(row)) {
      this.loadingTreeRows = false;
      this.app.spinner.hideSpinner("tree");
      return;
    }
    if (row.node == null || row.node.part == null) {
      this.loadingTreeRows = false;
      this.app.spinner.hideSpinner("tree");
      return;
    }
    if (this.isLeafRow(row)) {
      this.loadingTreeRows = false;
      this.app.spinner.hideSpinner("tree");
      return;
    }
    if (!this.openParts.has(row.node.part.partNumber)) {
      this.openParts.add(row.node.part.partNumber);
      await this.getRows(row);
    } else {
      this.openParts.delete(row.node.part.partNumber);
      this.removeFromItemList(row);
    }

    this.app.treeRow.isOpenRow(row);
    this.app.treeRow.isLeafRow(row);

    this.loadingTreeRows = false;
    this.app.spinner.hideSpinner("tree");
  }

  removeFromItemList(row: Row) {
    const result = this.findChildrenOfRow(row);
    const { index, childrenCount, childrenOpen } = result;
    /** remove the items(its children) found on the next level of the item clicked  */
    this.items.splice(index + 1, childrenCount);
    // Remove from openParts all children open from the current closed row
    this.removeChildrenFromOpenParts(childrenOpen);
  }

  /** if the item has been shown already, it has to be removed from the list of items */
  findChildrenOfRow(row: Row) {
    /** get the index of the current row */
    const index = this.items.indexOf(row);
    let childrenCount = 0;
    const childrenOpen = [];

    /** find the index until where the level is bigger than the level it was clicked on */
    for (let i = index + 1; i < this.items.length; i++) {
      const currentLevel = this.items[i].level;
      /** once the level is equal to the one that it was clicked then return */
      if (currentLevel === row.level) {
        break;
      }

      childrenOpen.push(this.items[i]);

      if (currentLevel && currentLevel > row.level) {
        childrenCount++;
      }
    }
    return { index, childrenCount, childrenOpen };
  }

  removeChildrenFromOpenParts(rows: Row[]) {
    const children: string[] = [];

    rows.forEach((row: Row) => {
      if (row.node == null || row.node.part == null) {
        return;
      }
      children.push(row.node.part._id);
    });

    this.openParts.forEach((part) => {
      if (children.includes(part)) {
        this.openParts.delete(part);
      }
    });
  }

  isOpenRow(row: Row) {
    if (row.node == null || row.node.part == null) {
      return false;
    }
    return this.openParts.has(row.node.part.partNumber);
  }

  isLeafRow(row: Row): boolean | null {
    const { node } = row;
    if (node == null) {
      return null;
    }
    if (row.expandable != null) {
      return !row.expandable;
    }
    let parts = Object.keys(node.part.parts ?? {});

    if (
      row.level !== 0 &&
      Object.keys(this.app.filterTree.filterValues).length > 0
    ) {
      const filteredPartNumbers = this.app.filterTree.result.parts.map(
        (part: any) => part._id
      );
      parts = parts.filter((partNumber) =>
        filteredPartNumbers.includes(partNumber)
      );
    }

    return parts.length === 0 && row.hasManufacturers !== true;
  }

  async getRows(row: Row) {
    if (row.node == null || row.node.part == null) {
      return;
    }

    const childRows = await this.getChildrenRows(row);
    let index = this.items.findIndex((r) => r === row);
    if (index !== -1) {
      this.items.splice(++index, 0, ...childRows);
    }
  }

  private async getChildrenRows(row: Row) {
    if (row.node == null || row.node.part == null) {
      return [];
    }
    const { items, missingPartNumbers } = await getTreeChildren(
      this.app.customers.expectCurrent,
      row.node.part.partNumber
    );
    let childrenRows = items;

    // If there is at least a filter selected the list of containing children has to be taken from the external-data-filter request directly
    if (
      row.level !== 0 &&
      Object.keys(this.app.filterTree.filterValues).length > 0
    ) {
      const filteredPartNumbers = this.app.filterTree.result.parts.map(
        (part: any) => part._id
      );
      childrenRows = childrenRows.filter(
        (child) =>
          child.node == null ||
          filteredPartNumbers.includes(child.node.part._id)
      );
    }

    childrenRows.forEach((childRow) => {
      childRow.level = row.level != null ? row.level + 1 : 1;

      if (childRow.node != null) {
        childRow.node.bom = [];
        if (childRow.node.part.totalMarketInv != null) {
          childRow.node.part.totalMarketInv =
            childRow.node.part.totalMarketInv.replace(
              /\B(?=(\d{3})+(?!\d))/g,
              ","
            );
        }
      }

      if (childRow.manufacturer != null) {
        const manufacturer = childRow.manufacturer;
        if (
          manufacturer.svhcItems != null &&
          (manufacturer as any).svhcItems !== ""
        ) {
          const substanceIdentificationArray: string[] = [];
          manufacturer.svhcItems.forEach((element: SVHCItem) => {
            if (
              element.substanceIdentification !== "" &&
              element.substanceIdentification != null
            ) {
              substanceIdentificationArray.push(
                element.substanceIdentification
              );
            }
          });
          (manufacturer as any).reachSubstances = [
            ...new Set(substanceIdentificationArray)
          ].join(", ");
        }

        if (manufacturer.totalMarketInv) {
          manufacturer.totalMarketInv = manufacturer.totalMarketInv.replace(
            /\B(?=(\d{3})+(?!\d))/g,
            ","
          );
        }
      }
    });

    if (missingPartNumbers.length > 0) {
      this.app.state.next({
        hasError: true,
        errorText: `Missing part references in tree configuration: ${missingPartNumbers.join(", ")}`
      });
    }

    if (items.length === 0) {
      row.expandable = false;
      this.openParts.delete(row.node.part.partNumber);
    }

    return childrenRows;
  }

  getHeaderStyle(column: string) {
    switch (column) {
      case this.app.fieldId.part.partNumber:
        return "medium-col";
      case this.app.fieldId.part.partsToVehicles:
      case this.app.fieldId.part.quantity:
        // case this.app.fieldId.part.detailView:
        return "small-col";
      case this.app.fieldId.manufacturer.purchasePartNumber:
      case this.app.fieldId.part.description:
      case this.app.fieldId.part.itemType:
      case this.app.fieldId.part.sapStatus:
        return "large-col";
      case this.app.fieldId.part.threadCase:
      case this.app.fieldId.part.matchedSystem:
      case this.app.fieldId.part.obsolescenceManagement:
        return "icon-column";
      default:
        return "";
    }
  }

  isPartRow(row: Row | null | undefined) {
    return row != null && row.node != null && row.node.part != null;
  }
  isManufacturerRow(row: Row | null | undefined) {
    return row != null && row.manufacturer != null && row.node == null;
  }

  /** moved to operations manager */
  // getManufacturersWithoutParts(parts: Part[], manufacturers: Manufacturer[]) {
  //   const { manufacturersWithoutNode } = getTree(parts, manufacturers);
  //   this.manufacturersWithoutParts = manufacturersWithoutNode;
  // }
}
